import * as tslib_1 from "tslib";
import { ErrorHandler, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { MatAutocomplete } from '@angular/material';
import { of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { AirportService } from '../../airport/airport.service';
import { GoogleService } from '../../google/google.service';
import { mergeStates, State } from '../state-view/state.model';
import { isString } from '../../util/helpers';
import { STATES } from '../../util/states';
import { Source } from '../models/source.model';
import { AviationEventFiltersService } from '../services/aviation-event-filters.service';
import { SearchFormType } from '../../search/models/search-form-type.model';
export var AutocompleteType;
(function (AutocompleteType) {
    AutocompleteType[AutocompleteType["Airports"] = 0] = "Airports";
    AutocompleteType[AutocompleteType["Google"] = 1] = "Google";
    AutocompleteType[AutocompleteType["Combined"] = 2] = "Combined";
})(AutocompleteType || (AutocompleteType = {}));
export var AutocompleteOptionType;
(function (AutocompleteOptionType) {
    AutocompleteOptionType[AutocompleteOptionType["Airport"] = 0] = "Airport";
    AutocompleteOptionType[AutocompleteOptionType["Location"] = 1] = "Location";
    AutocompleteOptionType[AutocompleteOptionType["Text"] = 2] = "Text";
    AutocompleteOptionType[AutocompleteOptionType["State"] = 3] = "State";
    AutocompleteOptionType[AutocompleteOptionType["ZipCode"] = 4] = "ZipCode";
})(AutocompleteOptionType || (AutocompleteOptionType = {}));
/**
 * List of ISO 3166-1 country codes to include in Google autocomplete results. This is limited to 5
 * country codes, and should include countries where we have at least some airport data. Here we include
 * the US, Mexico, and Canada, as well as the US Virgin Islands and Bahamas.
 */
export var COUNTRIES = ['US', 'MX', 'CA', 'VI', 'BS'];
var AutocompleteTriggerComponent = /** @class */ (function () {
    function AutocompleteTriggerComponent(_router, _airportService, _googleService, _errorHandler, _aviationEventFiltersService) {
        this._router = _router;
        this._airportService = _airportService;
        this._googleService = _googleService;
        this._errorHandler = _errorHandler;
        this._aviationEventFiltersService = _aviationEventFiltersService;
        this.queryType = AutocompleteType.Airports;
        this.state = State.Empty;
        this.international = false;
        this.currentSearchTab = SearchFormType.Airports;
        this.itemSelected = new EventEmitter();
        this.stateChange = new EventEmitter();
        this.airportState = State.Empty;
        this.googleState = State.Empty;
        this.isString = isString;
        this.searchFormType = SearchFormType;
        this.searchCtrl = new FormControl();
    }
    Object.defineProperty(AutocompleteTriggerComponent.prototype, "sanitizedQuery", {
        get: function () {
            return AirportService.sanitizeSearchTerm(this.query);
        },
        enumerable: true,
        configurable: true
    });
    AutocompleteTriggerComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.searchTerm$ = this.searchCtrl.valueChanges.pipe(
        // wait 300ms after each keystroke before considering the term
        debounceTime(300), 
        // ignore if next search term is same as previous
        distinctUntilChanged(), 
        // Selecting an autocomplete option sets the control value to the option object; only run suggestion
        // lookups for string input so a selection cannot re-trigger (and get stuck in) a loading state.
        filter(function (value) { return isString(value); }));
        this.aviationEventFilterSubscription = this._aviationEventFiltersService.aviationEventFilter.subscribe(function (ae) {
            _this.aviationEventFilter = ae;
        });
        if (this.queryType === AutocompleteType.Airports || this.queryType === AutocompleteType.Combined) {
            this.airportResults$ = this.searchTerm$.pipe(tap(function () {
                _this.airportState = State.Loading;
                _this._updateState();
            }), switchMap(function (query) { return _this.queryType === AutocompleteType.Combined
                ? _this._airportService.getAirportCompletions(query, 5)
                : _this._airportService.getAirportCompletions(query); }), tap(function () {
                _this.airportState = State.Content;
                _this._updateState();
            }), catchError(function (e) {
                _this._errorHandler.handleError(e);
                _this.airportState = State.Error;
                _this._updateState();
                return of([]);
            }));
        }
        if (this.queryType === AutocompleteType.Google || this.queryType === AutocompleteType.Combined) {
            this.googleResults$ = this.searchTerm$.pipe(tap(function () {
                _this.googleState = State.Loading;
                _this._updateState();
            }), switchMap(function (query) { return _this._googleService.getPlacePredictions({
                input: query ? query : ' ',
                types: ['(cities)'],
                componentRestrictions: {
                    country: COUNTRIES
                }
            }); }), tap(function () {
                _this.googleState = State.Content;
                _this._updateState();
            }), catchError(function (e) {
                _this._errorHandler.handleError(e);
                _this.googleState = State.Error;
                _this._updateState();
                return of([]);
            }));
        }
        if (this.queryType === AutocompleteType.Combined) {
            this.usStateResults$ = this.searchTerm$.pipe(map(function (query) {
                if (_this.queryType === AutocompleteType.Combined && isString(query)) {
                    return _this._addUSStates(query);
                }
            }), catchError(function (e) {
                _this._errorHandler.handleError(e);
                _this.googleState = State.Error;
                _this._updateState();
                return of([]);
            }));
        }
        if (this.queryType === AutocompleteType.Combined) {
            this.internationalCityResults$ = this.searchTerm$.pipe(tap(function () {
                _this.googleState = State.Loading;
                _this._updateState();
            }), switchMap(function (query) {
                var intResult = _this._googleService.getPlacePredictions({
                    input: query ? query : ' ',
                    types: ['(regions)']
                });
                var intResult2 = intResult.then(function (intValues) { return intValues.filter(function (item) { return !item.description.endsWith(', USA'); }).filter(function (item2) { return item2.types.includes('locality') || item2.types.includes('country'); }).sort(function (a, b) {
                    if (a.types.includes('locality') && !b.types.includes('locality')) {
                        return -1;
                    }
                    if (a.types.includes('country') && !b.types.includes('country')) {
                        return 1;
                    }
                    if (a.description < b.description) {
                        return -1;
                    }
                    if (a.description > b.description) {
                        return 1;
                    }
                    return 0;
                }); });
                return intResult2;
            }), tap(function () {
                _this.googleState = State.Content;
                _this._updateState();
            }), catchError(function (e) {
                _this._errorHandler.handleError(e);
                _this.googleState = State.Error;
                _this._updateState();
                return of([]);
            }));
        }
    };
    AutocompleteTriggerComponent.prototype.ngOnDestroy = function () {
        this.aviationEventFilterSubscription.unsubscribe();
    };
    AutocompleteTriggerComponent.prototype._addUSStates = function (query) {
        var filteredStates = [];
        if (query.length < 2) {
            return filteredStates;
        }
        if (query.length === 2) {
            filteredStates = STATES.filter(function (item) { return item.stateCode.toLowerCase().startsWith(query.toLowerCase()); });
        }
        else {
            filteredStates = STATES.filter(function (item) { return item.name.toLowerCase().startsWith(query.toLowerCase()); });
        }
        return filteredStates;
    };
    AutocompleteTriggerComponent.prototype._updateState = function () {
        var previousState = this.state;
        this.state = mergeStates(this.airportState, this.googleState);
        if (previousState !== this.state) {
            this.stateChange.emit(this.state);
        }
    };
    AutocompleteTriggerComponent.prototype.displayFunc = function (value) {
        if (value) {
            if (value.identifier) {
                if (value.icaoId != null && value.icaoId.toUpperCase().startsWith('ZZ')) {
                    return "" + value.name;
                }
                return (value.icaoId || value.faaId || '') + ": " + value.name;
            }
            else if (value.description) {
                return value.description;
            }
        }
        return '';
    };
    AutocompleteTriggerComponent.prototype.getAirportText = function (result) {
        if (result.icaoId != null && result.icaoId.toUpperCase().startsWith('ZZ')) {
            return "" + result.name;
        }
        return (result.icaoId || result.faaId || '') + ": " + result.name;
    };
    AutocompleteTriggerComponent.prototype.getCityStateText = function (result) {
        var city = result.city;
        var state = result.stateProvince;
        if (city || state) {
            return '(' + [city, state].filter(function (v) { return v; }).join(', ') + ')';
        }
        return '';
    };
    AutocompleteTriggerComponent.prototype.getCityText = function () {
        if (this.queryType === AutocompleteType.Combined) {
            return ' (City)';
        }
        return '';
    };
    AutocompleteTriggerComponent.prototype.getStateTerritoryText = function () {
        return ' (State/Territory)';
    };
    AutocompleteTriggerComponent.prototype.getInternationalText = function (result) {
        if (result.types.includes('country')) {
            return ' (Country)';
        }
        if (result.types.includes('locality')) {
            return ' (City)';
        }
        return '';
    };
    AutocompleteTriggerComponent.prototype.showAiports = function () {
        return (this.queryType === AutocompleteType.Airports || this.queryType === AutocompleteType.Combined) && !this.international;
    };
    AutocompleteTriggerComponent.prototype.showGoogle = function () {
        return (this.queryType === AutocompleteType.Google || this.queryType === AutocompleteType.Combined) && !this.international;
    };
    AutocompleteTriggerComponent.prototype.showInternational = function () {
        return this.queryType === AutocompleteType.Combined && this.international;
    };
    /**
     * True when the entry is exactly five digits, i.e. a ZIP code.
     */
    AutocompleteTriggerComponent.prototype.isZipQuery = function () {
        return /^\d{5}$/.test(this.sanitizedQuery);
    };
    /**
     * Whether to surface a dedicated "ZIP Code" section. A five-digit entry in the Flying Club Finder is treated
     * as a ZIP code: the API resolves its centroid from the WebApps ZIPCodes table and runs a radius search
     * (see FlyingClubManager.NormalizeZipCode on the server). When shown, it replaces the airport/location
     * suggestions so it is clear a proximity search will be performed.
     */
    AutocompleteTriggerComponent.prototype.showZipCode = function () {
        return this.isZipQuery()
            && (this.currentSearchTab === SearchFormType.FlyingClubs
                || this.currentSearchTab === SearchFormType.FlightSchools
                || this.currentSearchTab === SearchFormType.Universities
                || this.currentSearchTab === SearchFormType.Fbos
                || this.currentSearchTab === SearchFormType.ServiceRepair
                || this.currentSearchTab === SearchFormType.RentalSales);
    };
    Object.defineProperty(AutocompleteTriggerComponent.prototype, "zipSearchLabel", {
        /**
         * Label for the ZIP Code suggestion, naming the entity being searched near the ZIP so the option reads
         * correctly on each finder (e.g. "Flight schools near ZIP 21701").
         */
        get: function () {
            if (this.currentSearchTab === SearchFormType.FlightSchools) {
                return 'Flight schools';
            }
            if (this.currentSearchTab === SearchFormType.Universities) {
                return 'Universities';
            }
            if (this.currentSearchTab === SearchFormType.Fbos) {
                return 'FBOs';
            }
            if (this.currentSearchTab === SearchFormType.ServiceRepair) {
                return 'Service/repair businesses';
            }
            if (this.currentSearchTab === SearchFormType.RentalSales) {
                return 'Rentals/sales businesses';
            }
            return 'Flying clubs';
        },
        enumerable: true,
        configurable: true
    });
    AutocompleteTriggerComponent.prototype.onItemSelected = function (e) {
        var value = e.option.value;
        // Selecting an option ends the in-progress suggestion lookups. Clear any pending loading state so the
        // field spinner does not keep spinning when a suggestion query was still in flight at selection time
        // (e.g. picking a fast Google location result before the slower airport lookup returned).
        this.airportState = State.Content;
        this.googleState = State.Content;
        this._updateState();
        // When the ZIP Code section is showing it is the only selectable option, so any selection is the ZIP.
        // Emit a dedicated ZipCode option so consumers keep the Flying Club context (rather than falling into
        // their generic text/airport handling).
        if (this.showZipCode() && isString(value)) {
            this.itemSelected.emit({
                type: AutocompleteOptionType.ZipCode,
                query: value
            });
            return;
        }
        if (value.identifier) {
            this.itemSelected.emit({
                type: AutocompleteOptionType.Airport,
                airport: value,
                query: this.query
            });
        }
        else if (value.place_id) {
            this.itemSelected.emit({
                type: AutocompleteOptionType.Location,
                location: value,
                query: this.query
            });
        }
        else if (value.stateCode) {
            this.itemSelected.emit({
                type: AutocompleteOptionType.State,
                state: value,
                query: value.stateCode
            });
        }
        else if (isString(value) && value.length) {
            this.itemSelected.emit({
                type: AutocompleteOptionType.Text,
                query: value
            });
        }
    };
    /**
     * Enter key handler for the autocomplete control. This will take the user directly to the airport detail
     * page if the search term entered is an exact match on the identifier (either ICAO or FAA). Otherwise, it will
     * navigate the user to the search page with the provided term. Note that this method must be called by the
     * consumer of the autocomplete trigger component.
     * @param query {SearchQuery} Search query model. Only the term needs to be filled in, but all other search
     *    parameters will be serialized if provided and passed to the search page (in case where the search term
     *    is not an exact match on the identifier).
     * @return {Promise<void>}
     */
    AutocompleteTriggerComponent.prototype.submit = function (query) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var term, result, params;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        term = query.sanitizedTerm.toUpperCase();
                        if (!(term && AirportService.isAirportIdentifier(term))) return [3 /*break*/, 2];
                        return [4 /*yield*/, this._airportService.getAirport(term)];
                    case 1:
                        result = _a.sent();
                        if (result && query.type !== SearchFormType.AviationEvents && query.type !== SearchFormType.FlyingClubs
                            && query.type !== SearchFormType.FlightSchools && query.type !== SearchFormType.Universities
                            && query.type !== SearchFormType.Fbos && query.type !== SearchFormType.ServiceRepair
                            && query.type !== SearchFormType.RentalSales) {
                            if (result.sourceId === Source.CaribbeanGuide || result.sourceId === Source.BahamasGuide) {
                                this._router.navigate(['/airports', term, 'island']);
                            }
                            else {
                                this._router.navigate(['/airports', term]);
                            }
                            return [2 /*return*/];
                        }
                        _a.label = 2;
                    case 2:
                        query.aviationEventsFilter = this.aviationEventFilter;
                        params = query.encode();
                        this._router.navigate(['/search'], { queryParams: params });
                        return [2 /*return*/];
                }
            });
        });
    };
    return AutocompleteTriggerComponent;
}());
export { AutocompleteTriggerComponent };
